import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isLinkNode, LinkNode } from '@lexical/link';
import { $isElementNode, type LexicalNode } from 'lexical';
import { useEffect } from 'react';
import { sanitizeLinkUrl } from '@/components/common/lexical-links/sanitizeLinkUrl';

/**
 * Neutralizes a single link node: rewrites a fixable URL, or unwraps the link
 * to plain text when the scheme is disallowed (so a `javascript:` href can never
 * survive on an anchor). Must run inside an editor update.
 */
export function $neutralizeUnsafeLink(node: LinkNode): void {
  const url = node.getURL();
  const safe = sanitizeLinkUrl(url);

  if (safe === '') {
    // Disallowed scheme — drop the link, keep its text content in place.
    const children = node.getChildren();
    for (let i = children.length - 1; i >= 0; i--) {
      node.insertAfter(children[i]);
    }
    node.remove();
  } else if (safe !== url) {
    node.setURL(safe);
  }
}

/** Recursively sanitize every link under `node`. Must run inside an update. */
export function $sanitizeLinks(node: LexicalNode): void {
  if ($isLinkNode(node)) {
    $neutralizeUnsafeLink(node);
    return;
  }
  if ($isElementNode(node)) {
    // Snapshot children first; $neutralizeUnsafeLink can restructure the tree.
    for (const child of node.getChildren()) {
      $sanitizeLinks(child);
    }
  }
}

/**
 * Defense-in-depth against link-based XSS. A node transform catches links
 * created by typing, pasting, or programmatic edits; the initial markdown load
 * is sanitized separately (see `$loadMarkdown`) because the initializer runs
 * before this transform is registered.
 */
export function SanitizeLinksPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerNodeTransform(LinkNode, $neutralizeUnsafeLink);
  }, [editor]);

  return null;
}
